import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, CheckCircle, Clock, Pause, TrendingUp } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAffiliates() {
  const { user } = useContext(AuthContext);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Admin-only page
  if (!user?.is_internal_admin && user?.role !== 'admin') {
    return <Navigate to="/app/admin" replace />;
  }

  useEffect(() => {
    fetchAffiliates();
    fetchStats();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const res = await fetch(`${API}/affiliates/admin/all`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAffiliates(data.affiliates || []);
      }
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/admin/commissions/stats`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStatusChange = async (affiliateId, newStatus) => {
    try {
      const res = await fetch(`${API}/affiliates/admin/${affiliateId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Affiliate ${newStatus}`);
        await fetchAffiliates();
        await fetchStats();
      } else {
        toast.error('Failed to update affiliate status');
      }
    } catch (error) {
      toast.error('Error updating affiliate');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30', icon: Clock },
      approved: { color: 'bg-green-500/10 text-green-500 border-green-500/30', icon: CheckCircle },
      paused: { color: 'bg-gray-500/10 text-gray-500 border-gray-500/30', icon: Pause }
    };
    
    const { color, icon: Icon } = config[status] || config.pending;
    
    return (
      <Badge className={`${color} border px-3 py-1`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Affiliate Management</h1>
        <p className="text-[#8A93A6]">Admin view - Approve, monitor, and manage all affiliates</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6]">Total Affiliates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats.total_affiliates || 0}</div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6]">Pending Payouts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-500">${(stats.total_pending || 0).toFixed(2)}</div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6]">Total Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">${(stats.total_paid || 0).toFixed(2)}</div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6]">Active Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats.active_referrals || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Affiliates List */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>All Affiliates</CardTitle>
        </CardHeader>
        <CardContent>
          {affiliates.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-[#8A93A6] mx-auto mb-4" />
              <p className="text-[#8A93A6]">No affiliates registered yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {affiliates.map((affiliate) => (
                <div key={affiliate.id} className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-semibold text-white">{affiliate.user_name || 'Unknown'}</p>
                      {getStatusBadge(affiliate.status)}
                    </div>
                    <p className="text-sm text-[#8A93A6]">{affiliate.user_email}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-[#8A93A6]">
                      <span>Code: <span className="text-white font-mono">{affiliate.referral_code}</span></span>
                      <span>Referrals: <span className="text-white">{affiliate.total_referrals || 0}</span></span>
                      <span>Earnings: <span className="text-green-500">${(affiliate.total_earnings || 0).toFixed(2)}</span></span>
                      <span>Pending: <span className="text-yellow-500">${(affiliate.pending_payout || 0).toFixed(2)}</span></span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {affiliate.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(affiliate.id, 'approved')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Approve
                      </Button>
                    )}
                    {affiliate.status === 'approved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange(affiliate.id, 'paused')}
                      >
                        Pause
                      </Button>
                    )}
                    {affiliate.status === 'paused' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(affiliate.id, 'approved')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Resume
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
